import React, { useEffect, useState } from 'react';
import { PhoneCall, X } from 'lucide-react';
import { callingAPI } from '../api';

// Explicit caller + device selection for a delegated Click-to-Call. Nothing is dialled until the
// chosen caller and masked device are confirmed on screen.
export const CallerDeviceDialog = ({ selection, onClose, onConfirm, onPreview }) => {
  const [callers, setCallers] = useState([]);
  const [callerId, setCallerId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    callingAPI.c2cCallers()
      .then(({ data }) => { if (alive) { setCallers(data.callers || []); setLoading(false); } })
      .catch(() => { if (alive) { setCallers([]); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const caller = callers.find((c) => c.employee_id === callerId);
  const devices = caller?.devices || [];
  const needsDevice = devices.length > 1;
  const ready = !!callerId && (!needsDevice || !!deviceId);

  useEffect(() => {
    setPreview(null);
    if (!ready) return;
    let alive = true;
    onPreview(callerId, needsDevice ? deviceId : (devices[0]?.device_id || ''))
      .then((p) => { if (alive) setPreview(p); });
    return () => { alive = false; };
  }, [callerId, deviceId, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" data-testid="caller-device-dialog">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-[#131A2B]">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Choose calling employee</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {selection?.reason === 'DEVICE_SELECTION_REQUIRED'
                ? 'This employee has more than one active device — pick the exact phone.'
                : 'This lead belongs to another employee. The call is never routed automatically — select who dials.'}
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="caller-device-close" className="rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="mb-1 block text-xs font-medium text-muted-foreground">Calling employee</label>
        <select data-testid="caller-select" value={callerId} disabled={loading}
          onChange={(e) => { setCallerId(e.target.value); setDeviceId(''); }}
          className="mb-3 w-full rounded-lg border border-slate-200 bg-transparent px-3 py-2 text-sm dark:border-slate-700">
          <option value="">{loading ? 'Loading…' : 'Select employee'}</option>
          {callers.map((c) => (
            <option key={c.employee_id} value={c.employee_id}>{c.name} · {c.devices.length} device(s)</option>
          ))}
        </select>

        {needsDevice && (
          <>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Target device</label>
            <select data-testid="device-select" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}
              className="mb-3 w-full rounded-lg border border-slate-200 bg-transparent px-3 py-2 text-sm dark:border-slate-700">
              <option value="">Select device</option>
              {devices.map((d) => (
                <option key={d.device_id} value={d.device_id}>{d.masked_device}{d.is_default ? ' · default' : ''}</option>
              ))}
            </select>
          </>
        )}

        {preview && !preview.error && (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
            data-testid="caller-device-preview">
            Will ring <span className="font-semibold">{preview.caller_name}</span> on{' '}
            <span className="font-semibold">{preview.masked_device}</span>
            {selection?.lead_name ? <> for <span className="font-semibold">{selection.lead_name}</span></> : null}
          </div>
        )}
        {preview?.error && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300"
            data-testid="caller-device-error">{preview.error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="caller-device-cancel"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">Cancel</button>
          <button type="button" data-testid="caller-device-confirm"
            disabled={!ready || !preview || !!preview?.error}
            onClick={() => onConfirm(callerId, needsDevice ? deviceId : (devices[0]?.device_id || ''))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
            <PhoneCall className="h-4 w-4" />Place call
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallerDeviceDialog;
